import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import getSiteSdmList from "../http/getSiteSdmList";
import { SdmOffer, SdmSiteOffer } from "../models/site";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

interface UseSiteSdmProps {
  siteId?: string;
  getAllResults?: boolean;
  onlyLinkedOrgs?: boolean;
  isWired?: boolean;
  userOrgId?: string;
}

const useSiteSdm = ({
  siteId,
  getAllResults,
  onlyLinkedOrgs = false,
  isWired = false,
  userOrgId,
}: UseSiteSdmProps) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  // Note: userOrgId is a required parameter for the customer app
  // usage of this API. It is not required for the admin app.

  const fetchSiteSdmList: UseQueryResult<
    | { result: { buy?: SdmOffer; sell?: SdmOffer } }
    | { results: SdmSiteOffer[] },
    unknown
  > = useQuery<
    | { result: { buy?: SdmOffer; sell?: SdmOffer } }
    | { results: SdmSiteOffer[] },
    unknown
  >(
    ["site", siteId, "sdmList", isWired],
    async () => {
      return getSiteSdmList(
        await getAuthTokenBlocking(getToken, relogin),
        siteId!,
        getAllResults,
        onlyLinkedOrgs,
        userOrgId,
        isWired
      );
    },
    { enabled: !!siteId }
  );

  let bestBuyOffer: SdmOffer | undefined = undefined;
  let bestSellOffer: SdmOffer | undefined = undefined;
  if (fetchSiteSdmList.data) {
    if ("result" in fetchSiteSdmList.data) {
      bestBuyOffer = fetchSiteSdmList.data?.result?.buy;
      bestSellOffer = fetchSiteSdmList.data?.result?.sell;
    }
  }

  return {
    fetchSiteSdmList,
    bestBuyOffer,
    bestSellOffer,
  };
};

export default useSiteSdm;
